import { useEffect, useState } from 'react';
import { recruitingApi, onboardingApi } from '../../../api/recruiting.js';

export default function Offers() {
  const [items, setItems] = useState([]);
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [hireFor, setHireFor] = useState(null); // { offerId, applicationId }
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    recruitingApi
      .listOffers()
      .then((d) => setItems(d?.items || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    refresh();
    recruitingApi.listApplications({ stage: 'offer' }).then((d) => setApplications(d?.items || [])).catch(() => {});
    onboardingApi.listTemplates().then((d) => setTemplates(d?.items || [])).catch(() => {});
  }, []);

  async function confirmHire(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await recruitingApi.hire(hireFor.applicationId, {
        startOnboarding: true,
        onboardingTemplateId: fd.get('templateId') || null,
      });
      setHireFor(null);
      setNotice('Candidate hired — employee created and onboarding journey started.');
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Hire failed');
    }
  }

  async function createOffer(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await recruitingApi.createOffer({
        applicationId: fd.get('applicationId'),
        salary: Number(fd.get('salary')) || null,
        currency: fd.get('currency') || 'IDR',
        startDate: fd.get('startDate') || null,
      });
      form.reset();
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create offer');
    }
  }

  async function act(fn, id) {
    try { await fn(id); refresh(); } catch (err) { setError(err.response?.data?.message || 'Action failed'); }
  }

  return (
    <section data-testid="recruiting-offers-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div><h1>Offers</h1><p className="muted">{items.length} offers</p></div>
        <button className="btn primary" data-testid="recruiting-offer-create" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Close' : '+ Compose Offer'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="recruiting-offer-error">{error}</div>}
      {notice && <div className="alert" data-testid="recruiting-hire-notice">{notice}</div>}

      {showForm && (
        <form onSubmit={createOffer} className="card form-card grid-form">
          <label>Application (offer stage)
            <select name="applicationId" data-testid="recruiting-offer-application-select" required>
              <option value="">—</option>
              {applications.map((a) => <option key={a.id} value={a.id}>{a.candidate_name} · {a.requisition_title}</option>)}
            </select>
          </label>
          <label>Salary<input name="salary" type="number" data-testid="recruiting-offer-salary-input" /></label>
          <label>Currency<input name="currency" defaultValue="IDR" /></label>
          <label>Start date<input name="startDate" type="date" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="recruiting-offer-submit">Create</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="recruiting-offer-list">
          <thead><tr><th>Salary</th><th>Status</th><th>Document</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} data-testid={`recruiting-offer-row-${o.id}`}>
                <td>{o.salary ? `${o.currency} ${Number(o.salary).toLocaleString()}` : '—'}</td>
                <td><span className={`chip status-${o.status}`} data-testid={`recruiting-offer-status-${o.id}`}>{o.status}</span></td>
                <td>
                  {o.offer_file_url
                    ? <a href={o.offer_file_url} target="_blank" rel="noreferrer" data-testid={`recruiting-offer-pdf-link-${o.id}`}>PDF</a>
                    : <button className="btn ghost" data-testid={`recruiting-offer-pdf-${o.id}`} onClick={() => act(recruitingApi.generateOfferPdf, o.id)}>Generate PDF</button>}
                </td>
                <td className="actions">
                  {o.status === 'draft' && <button className="btn" data-testid={`recruiting-offer-extend-${o.id}`} onClick={() => act((id) => recruitingApi.extendOffer(id), o.id)}>Extend</button>}
                  {o.status === 'extended' && <button className="btn primary" data-testid={`recruiting-offer-accept-${o.id}`} onClick={() => act(recruitingApi.acceptOffer, o.id)}>Accept</button>}
                  {o.status === 'extended' && <button className="btn" data-testid={`recruiting-offer-decline-${o.id}`} onClick={() => act(recruitingApi.declineOffer, o.id)}>Decline</button>}
                  {o.status === 'accepted' && <button className="btn primary" data-testid={`recruiting-offer-hire-${o.id}`} onClick={() => setHireFor({ offerId: o.id, applicationId: o.application_id })}>Hire</button>}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="muted">No offers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {hireFor && (
        <form onSubmit={confirmHire} className="card form-card grid-form" data-testid="recruiting-hire-form">
          <h3 className="full">Hire — creates an employee + onboarding journey</h3>
          <label>Onboarding template
            <select name="templateId" data-testid="recruiting-hire-template-select" defaultValue="">
              <option value="">— none —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <div className="full actions">
            <button className="btn" type="button" onClick={() => setHireFor(null)}>Cancel</button>
            <button className="btn primary" type="submit" data-testid="recruiting-hire-submit">Confirm hire</button>
          </div>
        </form>
      )}
    </section>
  );
}
